import { create } from 'zustand';
import { Hub } from 'aws-amplify/utils';
import { User } from '../types/user';
import * as authService from '../services/authService';
import * as userService from '../services/userService';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  /**
   * Set when a first-time Google user needs to confirm their display name.
   * Contains the name from the Google JWT (pre-fill value). Cleared once the
   * user confirms, or when they navigate away.
   */
  pendingNameConfirm: string | null;

  // Actions
  signUp: (email: string, password: string, name: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendVerificationCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (redirectAfter?: string, nickname?: string) => Promise<void>;
  confirmDisplayName: (name: string) => Promise<void>;
  signOut: () => Promise<void>;
  loadUser: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  error: null,
  pendingNameConfirm: null,

  signUp: async (email: string, password: string, name: string) => {
    set({ isLoading: true, error: null });
    try {
      await authService.signUp({ email, password, name });

      // Do NOT set user or create the backend record here — the user has no JWT
      // until they confirm their email. The backend record is created on first
      // sign-in via the 404-recovery path in signIn/loadUser.
      set({ isLoading: false });
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Signup failed',
        isLoading: false
      });
      throw error;
    }
  },

  confirmSignUp: async (email: string, code: string) => {
    set({ isLoading: true, error: null });
    try {
      await authService.confirmSignUp(email, code);
      set({ isLoading: false });
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Verification failed',
        isLoading: false,
      });
      throw error;
    }
  },

  resendVerificationCode: async (email: string) => {
    set({ isLoading: true, error: null });
    try {
      await authService.resendConfirmationCode(email);
      set({ isLoading: false });
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Failed to resend code',
        isLoading: false,
      });
      throw error;
    }
  },

  forgotPassword: async (email: string) => {
    set({ isLoading: true, error: null });
    try {
      await authService.forgotPassword(email);
      set({ isLoading: false });
    } catch (error: unknown) {
      set({ error: error instanceof Error ? error.message : 'Failed to send reset code', isLoading: false });
      throw error;
    }
  },

  resetPassword: async (email: string, code: string, newPassword: string) => {
    set({ isLoading: true, error: null });
    try {
      await authService.confirmForgotPassword(email, code, newPassword);
      set({ isLoading: false });
    } catch (error: unknown) {
      set({ error: error instanceof Error ? error.message : 'Failed to reset password', isLoading: false });
      throw error;
    }
  },

  signIn: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      // Sign in with Cognito
      await authService.signIn({ email, password });

      // Fetch user from our database, recovering if the backend record was
      // never created (e.g. network error during the original signup).
      let user: User;
      try {
        user = await userService.getCurrentUser();
      } catch (err: unknown) {
        if ((err as { response?: { status?: number } })?.response?.status !== 404) throw err;
        // Orphaned account — backend creates the record from the verified JWT.
        await userService.registerUser();
        user = await userService.getCurrentUser();
      }

      set({ user, isLoading: false });
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Login failed',
        isLoading: false
      });
      throw error;
    }
  },

  signInWithGoogle: async (redirectAfter?: string, nickname?: string) => {
    set({ isLoading: true, error: null });
    try {
      await authService.signInWithGoogle(redirectAfter, nickname);
      // Page will redirect to Cognito Hosted UI — no state update needed here
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Google sign-in failed',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Called from the "Confirm your display name" screen on AuthCallbackPage.
   * Registers the backend user with the chosen name and completes sign-in.
   */
  confirmDisplayName: async (name: string) => {
    set({ isLoading: true, error: null });
    try {
      await userService.registerUser(name);
      const user = await userService.getCurrentUser();
      set({ user, isLoading: false, pendingNameConfirm: null });
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Failed to confirm display name',
        isLoading: false,
      });
      throw error;
    }
  },

  signOut: async () => {
    await authService.signOut();
    set({ user: null, error: null, pendingNameConfirm: null });
  },

  loadUser: async () => {
    set({ isLoading: true, error: null });
    try {
      const userInfo = await authService.getCurrentUserInfo();

      if (!userInfo) {
        set({ user: null, isLoading: false });
        return;
      }

      // Try to fetch the existing backend record
      let user: User;
      try {
        user = await userService.getCurrentUser();
      } catch (err: unknown) {
        if ((err as { response?: { status?: number } })?.response?.status !== 404) throw err;

        // New user — no backend record yet. Determine what name to use.
        const pendingNickname = sessionStorage.getItem('pending_nickname');
        sessionStorage.removeItem('pending_nickname');

        if (pendingNickname) {
          // Nickname was stored before the OAuth redirect — use it directly.
          await userService.registerUser(pendingNickname);
          user = await userService.getCurrentUser();
        } else if (window.location.pathname === '/auth/callback') {
          // First-time Google user arriving via login page (no stored nickname).
          // Pause registration and show the "Confirm your display name" screen.
          set({ pendingNameConfirm: userInfo.name || '', isLoading: false });
          return;
        } else {
          // Orphaned email account — register using JWT name (existing behaviour).
          await userService.registerUser();
          user = await userService.getCurrentUser();
        }
      }

      set({ user, isLoading: false });
    } catch {
      set({ user: null, isLoading: false });
    }
  },

  clearError: () => set({ error: null }),
}));

// Register the Hub listener at module level (not inside a React useEffect) so
// it is guaranteed to be in place before Amplify finishes the OAuth code
// exchange and emits signInWithRedirect. A useEffect-based listener can miss
// the event if the token exchange completes before React mounts.
//
// Skip in local dev mode — Amplify is bypassed entirely there.
if (!import.meta.env.VITE_AWS_ENDPOINT_URL) {
  Hub.listen('auth', ({ payload }) => {
    if (payload.event === 'signInWithRedirect') {
      useAuthStore.getState().loadUser();
    }
    if (payload.event === 'signInWithRedirect_failure') {
      useAuthStore.setState({ error: 'Google sign-in failed. Please try again.', isLoading: false });
    }
  });
}
